import { defineComponent as _defineComponent } from 'vue'

// Badge is exported from components/index.js but NOT registered by
// globalComponents.js; register locally like other consumers. The explicit
// .vue extension matters in lang="ts" blocks: TS only matches the
// shims-vue.d.ts '*.vue' declaration when the extension is present.
import Badge from '@/components/Badge.vue'

const __default__ = {
  name: 'affiliate-detail-drawer',
  components: { badge: Badge }
}

import { ref, computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatCurrency, getGroupSettings } from '@/util/currencyFormat'
import { formatGroupDate } from '@/plugins/dates'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  show: { type: Boolean, default: false },
  affiliate: { type: Object, default: null },
  link: { type: String, default: '' },
  canUpdate: { type: Boolean, default: false },
  tiers: { type: Array, default: () => [] }
},
  emits: ['close', 'changed'],
  setup(__props, { emit }) {

const props = __props





const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const group = JSON.parse(localStorage.getItem('group') || '{}')
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${group.id}`

const loadingPerformance = ref(false)
const sections = ref({ overview: true, performance: false, ledger: false, payout: false })
const savingTier = ref(false)
const selectedTierId = ref<any>(null)

// Archived tiers are hidden except the one currently assigned.
const tierOptions = computed(() =>
  (props.tiers as any[]).filter((t: any) => t.is_archived === 0 || t.id === selectedTierId.value))

const currentTierName = computed(() => {
  const match = (props.tiers as any[]).find((t: any) => t.id === selectedTierId.value)
  if (match) return match.name
  return (props.affiliate && props.affiliate.tier_name) || 'No tier'
})

async function onTierChange(tierId: any) {
  if (tierId === selectedTierId.value || savingTier.value) return
  const previous = selectedTierId.value
  savingTier.value = true
  selectedTierId.value = tierId
  try {
    const config = await authConfig()
    await axios.put(`${baseUrl}/affiliates/${props.affiliate.id}`, { affiliate_tier_id: tierId }, config)
    proxy.$notify({ type: 'success', message: 'Tier updated', icon: 'tim-icons icon-check-2' })
    emit('changed')
  } catch (err: any) {
    selectedTierId.value = previous
    proxy.$notify({ type: 'danger', message: errMessage(err, 'Failed to update tier'), icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    savingTier.value = false
  }
}
const performance = ref<any>(null)
const ledger = ref<any[]>([])
const recordingPayout = ref(false)
const payoutErr = ref<string | boolean>(false)
const payoutForm = ref<any>(emptyPayoutForm())
const attaching = ref(false)
const attachOpen = ref(false)
// base-input's `error` prop is typed String; never bind false to it.
const attachErr = ref('')
const attachForm = ref<any>({ order_number: '' })

const methodOptions = [
  { value: 'zelle', label: 'Zelle' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'other', label: 'Other' }
]

const typeLabels: any = { influencer: 'Influencer', local_business: 'Local business', player: 'Player' }

// House rule: money math in integer cents via exponential notation.
function toCents(x: any) {
  const n = parseFloat(x)
  if (Number.isNaN(n)) return 0
  if (Math.abs(n) < 0.005) return 0
  return Math.round(Number(n + 'e2'))
}

const owedCents = computed(() => (performance.value ? toCents(performance.value.owed) : 0))

const removing = ref(false)
// The earned row a staff member is about to reverse; null closes the dialog.
const removePrompt = ref<any>(null)

// Transactions that already carry a reversed row; earned rows in this set get
// no Remove link and render struck through.
const reversedTransactionIds = computed(() => {
  const ids = new Set<number>()
  for (const entry of ledger.value) {
    if (entry.entry_type === 'reversed' && entry.transaction_id != null) ids.add(Number(entry.transaction_id))
  }
  return ids
})

function isReversed(entry: any) {
  return entry.transaction_id != null && reversedTransactionIds.value.has(Number(entry.transaction_id))
}

function canRemove(entry: any) {
  return props.canUpdate && entry.entry_type === 'earned' && entry.transaction_id != null && !isReversed(entry)
}

// Select mode for moving earned entries to another tier.
const retierMode = ref(false)
const retierTierId = ref<any>(null)
const selectedIds = ref<Set<number>>(new Set())
const previewing = ref(false)
const retiering = ref(false)
// Preview response from the lambda; null closes the confirm dialog.
const retierPrompt = ref<any>(null)

// Archived tiers cannot receive entries; the lambda refuses them.
const activeTierOptions = computed(() => (props.tiers as any[]).filter((t: any) => t.is_archived === 0))

const eligibleEntries = computed(() => ledger.value.filter((e) => canRemove(e)))

function isSelected(entry: any) {
  return selectedIds.value.has(Number(entry.transaction_id))
}

// Vue 2.7 does not track Set mutation; every change reassigns a new Set.
function toggleSelected(entry: any, on: boolean) {
  const next = new Set(selectedIds.value)
  const id = Number(entry.transaction_id)
  if (on) next.add(id); else next.delete(id)
  selectedIds.value = next
}

function selectAll() {
  selectedIds.value = new Set(eligibleEntries.value.map((e) => Number(e.transaction_id)))
}


function openRetier() {
  retierMode.value = true
  const current = activeTierOptions.value.find((t: any) => t.id === selectedTierId.value)
  retierTierId.value = current ? current.id : null
  selectedIds.value = new Set()
}

function closeRetier() {
  retierMode.value = false
  retierTierId.value = null
  selectedIds.value = new Set()
  retierPrompt.value = null
}

function pluralEntries(n: number) {
  return n === 1 ? '1 entry' : `${n} entries`
}

const owedAfterRemovalCents = computed(() =>
  removePrompt.value ? owedCents.value - toCents(removePrompt.value.commission_amount) : 0)

function centsToDisplay(cents: number) {
  // formatMoney takes the dollar string convention the ledger rows use.
  return formatMoney((cents / 100).toFixed(2))
}

function emptyPayoutForm() {
  return { amount: null, payout_method: 'zelle', payout_reference: '', payout_note: '' }
}

function typeLabel(type: string) {
  return typeLabels[type] || type
}

function entryBadgeType(entryType: string) {
  if (entryType === 'earned') return 'success'
  if (entryType === 'reversed') return 'danger'
  return 'info'
}

function formatMoney(value: any) {
  return formatCurrency(parseFloat(value) || 0, {
    currency: group.currency,
    currencyFormat: group.currency_format,
    numberFormat: group.number_format
  })
}

function formatDate(d: any) {
  return formatGroupDate(d, { dayOfWeek: false })
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function errMessage(err: any, fallback: string) {
  const data = err?.response?.data
  if (data) {
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data
      if (parsed && parsed.error) return parsed.error
    } catch (e) { /* not JSON */ }
    if (typeof data === 'string' && data.length > 0 && data.length < 200) return data
  }
  return fallback
}

async function loadPerformance() {
  if (!props.affiliate) return
  loadingPerformance.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/affiliates/${props.affiliate.id}/performance`, config)
    performance.value = response.data
    ledger.value = response.data.ledger || []
    const stillEligible = new Set(ledger.value.filter((e) => canRemove(e)).map((e) => Number(e.transaction_id)))
    selectedIds.value = new Set([...selectedIds.value].filter((id) => stillEligible.has(id)))
    payoutForm.value = emptyPayoutForm()
    payoutForm.value.amount = toCents(response.data.owed) > 0 ? parseFloat(response.data.owed) : null
  } catch (err: any) {
    proxy.$notify({ type: 'danger', message: errMessage(err, 'Failed to load performance'), icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    loadingPerformance.value = false
  }
}

async function copyLink() {
  try {
    await navigator.clipboard.writeText(props.link)
    proxy.$notify({ type: 'success', message: 'Booking link copied', icon: 'tim-icons icon-check-2' })
  } catch (e) {
    proxy.$notify({ type: 'danger', message: 'Could not copy the link', icon: 'tim-icons icon-alert-circle-exc' })
  }
}

async function recordPayout() {
  payoutErr.value = false
  const amount = payoutForm.value.amount
  const amountCents = toCents(amount)
  if (amountCents <= 0) {
    payoutErr.value = 'Enter an amount greater than zero'
    return
  }
  if (amountCents > owedCents.value) {
    payoutErr.value = 'Payout exceeds amount owed'
    return
  }

  recordingPayout.value = true
  try {
    const config = await authConfig()
    const body = {
      amount,
      payout_method: payoutForm.value.payout_method,
      payout_reference: payoutForm.value.payout_reference || null,
      payout_note: payoutForm.value.payout_note || null
    }
    await axios.post(`${baseUrl}/affiliates/${props.affiliate.id}/payouts`, body, config)
    proxy.$notify({ type: 'success', message: 'Payout recorded', icon: 'tim-icons icon-check-2' })
    await loadPerformance()
    emit('changed')
  } catch (err: any) {
    payoutErr.value = errMessage(err, 'Failed to record payout')
    proxy.$notify({ type: 'danger', message: String(payoutErr.value), icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    recordingPayout.value = false
  }
}

function closeAttach() {
  attachOpen.value = false
  attachForm.value = { order_number: '' }
  attachErr.value = ''
}

async function attachTransaction() {
  attachErr.value = ''
  const orderNumber = String(attachForm.value.order_number || '').trim().replace(/^#/, '')
  if (!orderNumber) {
    attachErr.value = 'Enter a transaction number'
    return
  }
  attaching.value = true
  try {
    const config = await authConfig()
    await axios.post(`${baseUrl}/affiliates/${props.affiliate.id}/transactions`, { order_number: orderNumber }, config)
    proxy.$notify({ type: 'success', message: 'Transaction added to the ledger', icon: 'tim-icons icon-check-2' })
    closeAttach()
    await loadPerformance()
    emit('changed')
  } catch (err: any) {
    attachErr.value = errMessage(err, 'Failed to add the transaction')
    proxy.$notify({ type: 'danger', message: String(attachErr.value), icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    attaching.value = false
  }
}

async function removeTransaction() {
  const entry = removePrompt.value
  if (!entry || removing.value) return
  removing.value = true
  try {
    const config = await authConfig()
    await axios.delete(`${baseUrl}/affiliates/${props.affiliate.id}/transactions/${entry.transaction_id}`, config)
    proxy.$notify({ type: 'success', message: 'Transaction removed from the ledger', icon: 'tim-icons icon-check-2' })
    removePrompt.value = null
    await loadPerformance()
    emit('changed')
  } catch (err: any) {
    proxy.$notify({ type: 'danger', message: errMessage(err, 'Failed to remove the transaction'), icon: 'tim-icons icon-alert-circle-exc' })
    removePrompt.value = null
    // A 409 means someone else reversed it first; refresh so the link disappears.
    if (err?.response?.status === 409) await loadPerformance()
  } finally {
    removing.value = false
  }
}

async function previewRetier() {
  if (previewing.value || selectedIds.value.size === 0 || !retierTierId.value) return
  previewing.value = true
  try {
    const config = await authConfig()
    const body = { affiliate_tier_id: retierTierId.value, transaction_ids: [...selectedIds.value], preview: true }
    const response = await axios.put(`${baseUrl}/affiliates/${props.affiliate.id}/transactions`, body, config)
    retierPrompt.value = response.data
  } catch (err: any) {
    proxy.$notify({ type: 'danger', message: errMessage(err, 'Failed to calculate the new commission'), icon: 'tim-icons icon-alert-circle-exc' })
    if (err?.response?.status === 409) await loadPerformance()
  } finally {
    previewing.value = false
  }
}

async function applyRetier() {
  const prompt = retierPrompt.value
  if (!prompt || retiering.value) return
  retiering.value = true
  try {
    const config = await authConfig()
    const body = { affiliate_tier_id: prompt.tier.id, transaction_ids: prompt.rows.map((r: any) => r.transaction_id) }
    await axios.put(`${baseUrl}/affiliates/${props.affiliate.id}/transactions`, body, config)
    proxy.$notify({ type: 'success', message: `Tier updated on ${pluralEntries(prompt.rows.length)}`, icon: 'tim-icons icon-check-2' })
    closeRetier()
    await loadPerformance()
    emit('changed')
  } catch (err: any) {
    proxy.$notify({ type: 'danger', message: errMessage(err, 'Failed to change the tier'), icon: 'tim-icons icon-alert-circle-exc' })
    retierPrompt.value = null
    if (err?.response?.status === 409) await loadPerformance()
  } finally {
    retiering.value = false
  }
}

function onEscKey(e: KeyboardEvent) {
  if (e.key !== 'Escape') return
  if (retierPrompt.value) {
    if (!retiering.value) retierPrompt.value = null
    return
  }
  // While the remove confirm is open, ESC dismisses it rather than the drawer.
  if (removePrompt.value) {
    if (!removing.value) removePrompt.value = null
    return
  }
  emit('close')
}

watch(() => props.show, (val) => {
  if (val) {
    document.addEventListener('keydown', onEscKey)
    performance.value = null
    ledger.value = []
    selectedTierId.value = props.affiliate ? props.affiliate.affiliate_tier_id : null
    sections.value = { overview: true, performance: false, ledger: false, payout: false }
    closeAttach()
    removePrompt.value = null
    closeRetier()
    loadPerformance()
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})

return { __sfc: true,props, emit, proxy, companyId, group, baseUrl, loadingPerformance, sections, savingTier, selectedTierId, tierOptions, currentTierName, onTierChange, performance, ledger, recordingPayout, payoutErr, payoutForm, attaching, attachOpen, attachErr, attachForm, methodOptions, typeLabels, toCents, owedCents, removing, removePrompt, reversedTransactionIds, isReversed, canRemove, retierMode, retierTierId, selectedIds, previewing, retiering, retierPrompt, activeTierOptions, eligibleEntries, isSelected, toggleSelected, selectAll, openRetier, closeRetier, pluralEntries, owedAfterRemovalCents, centsToDisplay, emptyPayoutForm, typeLabel, entryBadgeType, formatMoney, formatDate, authConfig, errMessage, loadPerformance, copyLink, recordPayout, closeAttach, attachTransaction, removeTransaction, previewRetier, applyRetier, onEscKey, Badge }
}

})